<template>
  <v-app>
    <v-app-bar app color="#2A3C67" dark :elevation="5">
      <v-app-bar-nav-icon
        v-if="estaLogueado"
        @click.stop="drawer = !drawer"
      />

      <!-- Solo el nombre en el título -->
      <v-toolbar-title class="app-title">
        Terracon
      </v-toolbar-title>

      <v-spacer />

      <!-- Versión a la derecha -->
      <span class="app-version mr-2">
        v1.0.5
      </span>

      <!-- Usuario + menú con roles -->
      <v-menu v-if="estaLogueado" location="bottom end">
        <template #activator="{ props }">
          <div
            class="d-flex align-center pr-4 user-menu-activator"
            v-bind="props"
          >
            <v-icon class="mr-2" icon="mdi-account-circle" />
            <!--span class="font-weight-medium mr-1">
              {{ userName || 'Usuario' }}
            </span-->
            <v-icon icon="mdi-menu-down" size="18" />
          </div>
        </template>

        <v-card min-width="260" class="d-flex flex-column">

          <v-card-title class="py-3">
            <div class="text-subtitle-1 font-weight-bold">
              {{ userName || 'Usuario' }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ userDetailStore.userDetail?.email || userDetailStore.userDetail?.codi_user }}
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3 flex-grow-1">
            <div class="text-caption text-medium-emphasis mb-1">
              Roles
            </div>

            <div v-if="userRolesList.length">
              <v-chip
                v-for="(rol, idx) in userRolesList"
                :key="idx"
                size="small"
                class="ma-1"
                color="primary"
                variant="outlined"
              >
                {{ rol.name_rol }}
              </v-chip>
            </div>
            <div v-else class="text-caption text-medium-emphasis">
              Sin roles asignados
            </div>
          </v-card-text>

          <v-divider />

          <!-- 🔻 Log Out en el menú del usuario -->
          <v-card-actions class="py-2">
            <v-btn
              variant="text"
              color="red-lighten-2"
              prepend-icon="mdi-logout"
              class="text-capitalize"
              @click="logout"
            >
              Cerrar sesión
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- 🔴 Banner de estado de conexión -->
    <v-alert
      v-if="!isOnline"
      type="warning"
      variant="tonal"
      density="compact"
      class="mb-4 py-4 alerta-offline"
    >
      Estás sin conexión. Algunos cambios se enviarán cuando vuelvas a estar online.
    </v-alert>

    <v-navigation-drawer
      v-if="estaLogueado"
      class="v-navigation-drawer"
      v-model="drawer"
      temporary
    >
      <div class="d-flex flex-column h-100">
        <v-divider />

        <!-- Menú principal -->
        <v-list density="compact" nav class="flex-grow-1">
          <v-list-item
            to="/censo"
            value="censo"
            nav
          >
            <template #prepend>
              <v-badge
                :content="fmt(counts.calidad)"
                :model-value="counts.calidad > 0"
                color="error"
                floating
                location="top end"
                offset-x="6"
                offset-y="6"
              >
                <v-icon icon="mdi-forum" />
              </v-badge>
            </template>

            <v-list-item-title>Protocolos e Inspecciones</v-list-item-title>
          </v-list-item>

          <v-list-item
            to="/firmas"
            value="firmas"
            nav
          >
            <template #prepend>
              <v-badge
                :content="fmt(counts.documentos)"
                :model-value="counts.documentos > 0"
                color="error"
                floating
                location="top end"
                offset-x="6"
                offset-y="6"
              >
                <v-icon icon="mdi-key" />
              </v-badge>
            </template>

            <v-list-item-title>Firma Electronica</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="esSup"
            to="/proyectosProduccion"
            value="Proyectos Produccion"
            nav
          >
            <template #prepend>
              <v-icon icon="mdi-format-list-text" />
            </template>

            <v-list-item-title>Parte Producción Diaria</v-list-item-title>
          </v-list-item>
        </v-list>

        <v-divider />

        <!-- Log Out abajo -->
        <v-list density="compact" nav>
          <v-list-item @click="logout">
            <template #prepend>
              <v-icon icon="mdi-logout" />
            </template>
            <v-list-item-title>Cerrar sesión</v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </v-navigation-drawer>

    <v-main>
      <router-view />
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, provide, watchEffect, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import apiAxios from '@/services/api'

import useProfileStore from '@/store/profile.js'
import { useUserDetailStore } from '@/store/userDetail'
import { useApiWatcher } from '@/useApiWatcher.js'
import { useNetworkStatus } from '@/composables/useNetworkStatus'

/* eslint-disable */

/* ---------- stores y router ---------- */
const route = useRoute()
const router = useRouter()
const profileStore = useProfileStore()
const userDetailStore = useUserDetailStore()

/* ---------- estado online/offline ---------- */
const { isOnline } = useNetworkStatus()

/* ---------- layout / estado general ---------- */
const drawer = ref(false)
const message = ref(1)
provide('global-puerto', message)

/* ---------- IndexedDB (igual que antes) ---------- */
const indexedDB = window.indexedDB
const conexion = indexedDB.open('BHP', 1)

const roles = ref([]);
const rolesNombres = ref([]);

let db

conexion.onsuccess = () => {
  db = conexion.result
  console.log('Base de Datos abierta', db)
}

conexion.onupgradeneeded = (e) => {
  console.log(e)
  db = conexion.result
  console.log('Base de Datos creada', db)

  db.createObjectStore('Lotes', {
    keyPath: 'NUMERO_LOTE'
  })
  db.createObjectStore('LotesDetalles', {
    keyPath: 'NUMERO_LOTE'
  })
  db.createObjectStore('Inspecciones', {
    keyPath: 'numeroLote'
  })
}

conexion.onerror = (error) => {
  console.log('Error', error)
}

const estaLogueado = computed(() => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token')
  return !!token && !!userDetailStore.userDetail?.id_user
})

/* ---------- Axios instance para watcher ---------- */
const api = apiAxios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 10000
})

onMounted(async () => {
  await Promise.all([obtenerUsuariosRoles(), obtenerRoles()])
})

/* ---------- Notificaciones / counts ---------- */
const counts = reactive({
  calidad: 0,
  documentos: 0,
  enrolamiento: 0,
  medioAmbiente: 0
})

const AREA_ID_TO_KEY = {
  1: 'calidad',
  2: 'seguridad',
  3: 'medioAmbiente',
  4: 'enrolamiento',
  6: 'documentos'
}

function aplicarCountsDesdeNotifs (notifs) {
  counts.calidad = counts.documentos = counts.enrolamiento = counts.medioAmbiente = 0
  for (const n of (notifs || [])) {
    const idArea = Number(n?.json_data?.id_area)
    const key = AREA_ID_TO_KEY[idArea]
    if (key) counts[key]++
  }
}

/* ---------- useApiWatcher para notificaciones ---------- */
const watcher = useApiWatcher({
  url: '/servicio/leanglobal/obtenerNortificaciones',
  method: 'get',
  params: { id_user_target: userDetailStore.userDetail?.id_user },
  axiosInstance: api,
  intervalMs: 10000,
  maxIntervalMs: 60000,
  backoffFactor: 2,
  immediate: true
})

watchEffect(() => {
  const snap = watcher.data.value

  if (snap?.counts) {
    counts.calidad       = Number(snap.counts.calidad || 0)
    counts.documentos    = Number(snap.counts.documentos || 0)
    counts.enrolamiento  = Number(snap.counts.enrolamiento || 0)
    counts.medioAmbiente = Number(snap.counts.medioAmbiente || 0)
  } else if (Array.isArray(snap)) {
    aplicarCountsDesdeNotifs(snap)
  }
})

/* ---------- helpers ---------- */
function isActiveRoute (path) {
  return route.path === path
}

const fmt = (n) => (n > 99 ? '99+' : String(n))

const esSup = computed(() => {
  const u = userDetailStore.userDetail
  const roles = u?.usuarioRoles
  if (!Array.isArray(roles)) return false
  return roles.some(r =>
    typeof r === 'number'
      ? r === 11
      : r?.id_rol === 11 && (r?.id_user ?? u?.id_user) === u?.id_user
  )
})

/* ---------- usuario + roles para el menú ---------- */
const userName = computed(() => {
  const u = userDetailStore.userDetail || {}
  const nombre = `${u.name_frst ?? ''} ${u.apellido_pat ?? ''}`.trim()
  return nombre || u.codi_user || u.email || ''
})

const obtenerUsuariosRoles = async () => {
  try {
    const { data } = await apiAxios.get(
      '/servicio/leanglobal/obtenerUsuariosRoles'
    )
    roles.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error('roles error:', e)
    roles.value = []
  }
}

const obtenerRoles = async () => {
  try {
    const { data } = await apiAxios.get(
      '/servicio/leanglobal/obtenerRoles'
    )
    rolesNombres.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error('roles error:', e)
    rolesNombres.value = []
  }
}

// Mapa rápido: id_rol => { id_rol, name_rol, description }
const rolesMap = computed(() => {
  const map = new Map()
  for (const r of rolesNombres.value) {
    if (r?.id_rol != null) {
      map.set(String(r.id_rol), r)
    }
  }
  return map
})

// Lista de roles del usuario actual, con nombre y descripción
const userRolesList = computed(() => {
  const u = userDetailStore.userDetail
  const idUser = u?.id_user

  if (!idUser || !Array.isArray(roles.value)) return []

  // Filtramos los roles que corresponden al usuario actual
  const rolesUsuario = roles.value.filter(r =>
    String(r.id_user) === String(idUser)
  )

  const list = rolesUsuario
    .map(r => {
      const meta = rolesMap.value.get(String(r.id_rol))

      if (meta) {
        return {
          id_rol: meta.id_rol,
          name_rol: meta.name_rol,
          description: meta.description
        }
      }

      // Fallback por si el backend algún día trae name_rol directo en esta tabla
      if (r.name_rol || r.description) {
        return {
          id_rol: r.id_rol,
          name_rol: r.name_rol || `Rol ${r.id_rol ?? '?'}`,
          description: r.description || ''
        }
      }

      return {
        id_rol: r.id_rol,
        name_rol: `Rol ${r.id_rol ?? '?'}`,
        description: ''
      }
    })
    .filter(Boolean)

  // Evitar duplicados por id_rol
  const unique = new Map()
  for (const rol of list) {
    unique.set(String(rol.id_rol), rol)
  }

  return Array.from(unique.values())
})

const logout = () => {
  // ❌ ya NO usamos $reset, porque eso borra el userDetail persistido
  localStorage.removeItem('token')
  sessionStorage.removeItem('token')

  router.push({ path: '/login' })
}

</script>

<style scoped>
.v-navigation-drawer {
  padding-top: 10px;
}

.user-menu-activator {
  cursor: pointer;
  border-radius: 999px;
  padding: 4px 8px;
  transition: background-color 0.15s ease-in-out;
}

.user-menu-activator:hover {
  background-color: rgba(255, 255, 255, 0.12);
}

.app-title {
  white-space: nowrap;
}

.app-version {
  font-size: 0.75rem;
  opacity: 0.8;
  white-space: nowrap;
}

.alerta-offline {
  margin-top: 100px !important;
}
</style>
